import { useState, type ReactNode } from 'react'
import { SquarePlus, Table } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { ogsmErrorMessage } from '@/features/ogsm/errors'
import { EnterActualsDialog } from '@/components/custom/ogsm/enter-actuals-dialog'
import { ShowInactiveButton } from '@/components/custom/ogsm/inactive-toggle'
import { NewObjectiveDialog } from '@/components/custom/ogsm/new-objective-dialog'
import { OgsmBoardView } from '@/components/custom/ogsm/ogsm-board'
import { OgsmTitleRow } from '@/components/custom/ogsm/ogsm-title-row'
import { SessionBar } from '@/components/custom/ogsm/session-bar'
import { viewHasInactive } from '@/features/ogsm/inactive'
import type { ObjectiveDialogTarget } from '@/features/ogsm/new-objective'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import { findDrilledObjective } from '@/features/ogsm/search'
import { useOgsmApi } from '@/hooks/use-ogsm'
import {
  useActiveOgsmBoardQuery,
  useCreateOgsmSessionMutation,
} from '@/hooks/use-ogsm-data'
import type { OgsmSession } from '@/types/api'

export interface OgsmNavigation {
  session?: string
  objective?: string
}

export interface OgsmWorkspaceProps {
  companyId: string
  companyName: string
  sessionId?: string
  objectiveId?: string
  onNavigate: (next: OgsmNavigation) => void
  /** Root heading; defaults to the company name. */
  title?: string
  /** `portal`: an h2 under the page's h1 and never the (Greybull-only) session bar. */
  surface?: 'page' | 'portal'
  /** The viewer lacks the edit capability: portal `ogsm:edit:own`, internal `ogsm:edit:all`. */
  readOnlyViewer?: boolean
}

/** The title row's note, first match only. */
function sessionNote(
  session: OgsmSession,
  portal: boolean,
  readOnlyViewer: boolean
): string | null {
  // The backend marks every session not editable for a read-only caller, so the viewer note wins.
  if (readOnlyViewer) {
    return portal ? OGSM_COPY.portalReadOnly : OGSM_COPY.internalReadOnly
  }
  if (!session.editable) return OGSM_COPY.readOnly
  // G5: the portal has no session bar to say why editing stopped.
  if (portal && session.status === 'PUBLISHED') {
    return OGSM_COPY.portalPublished
  }
  return null
}

export function OgsmWorkspace({
  companyId,
  companyName,
  sessionId,
  objectiveId,
  onNavigate,
  title,
  surface = 'page',
  readOnlyViewer = false,
}: OgsmWorkspaceProps) {
  const api = useOgsmApi(companyId)
  // The URL wins; without one, show the newest session.
  const { sessions, board, activeId } = useActiveOgsmBoardQuery(api, sessionId)
  const createSession = useCreateOgsmSessionMutation(api)
  const portal = surface === 'portal'
  const [dialog, setDialog] = useState<ObjectiveDialogTarget | null>(null)
  const [actualsOpen, setActualsOpen] = useState(false)

  const boardData = board.data
  const drilled = boardData
    ? findDrilledObjective(boardData, objectiveId)
    : undefined
  const titleRow = (controls?: ReactNode) => (
    <OgsmTitleRow
      title={drilled?.title ?? title ?? companyName}
      headingLevel={portal ? 'h2' : 'h1'}
      onUpOneLevel={
        drilled
          ? () => onNavigate({ objective: drilled.parentId ?? undefined })
          : undefined
      }
    >
      {controls}
    </OgsmTitleRow>
  )

  if (sessions.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        {titleRow()}
        <Typography variant="muted">{OGSM_COPY.loading}</Typography>
      </div>
    )
  }
  if (sessions.isError || board.isError) {
    return (
      <div className="flex flex-col gap-4">
        {titleRow()}
        <Typography variant="muted" role="alert">
          {ogsmErrorMessage(sessions.error ?? board.error)}
        </Typography>
      </div>
    )
  }
  const data = sessions.data
  if (!data) return null
  const structureEditable = !!boardData?.session.structureEditable
  const note =
    boardData && sessionNote(boardData.session, portal, readOnlyViewer)

  const start = data.canCreateCurrentSession && (
    <div className="flex items-center gap-3">
      <Button
        onClick={() =>
          createSession.mutate(undefined, {
            onSuccess: (created) => onNavigate({ session: created.id }),
          })
        }
        disabled={createSession.isPending}
      >
        {OGSM_COPY.startSession(data.currentFiscalYear)}
      </Button>
      {createSession.error && (
        <Typography variant="muted" role="alert">
          {ogsmErrorMessage(createSession.error)}
        </Typography>
      )}
    </div>
  )

  if (data.sessions.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {titleRow()}
        <div className="flex flex-col items-start gap-3">
          <Typography variant="muted">{OGSM_COPY.noSessions}</Typography>
          {start}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {titleRow(
        <>
          <Select
            value={activeId ?? ''}
            onValueChange={(session) => onNavigate({ session })}
          >
            <SelectTrigger aria-label="Session" className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {data.sessions.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {OGSM_COPY.sessionLabel(s.fiscalYear)}
                  {s.editable ? OGSM_COPY.currentSuffix : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {note && <Typography variant="muted">{note}</Typography>}
          {boardData && viewHasInactive(boardData, drilled) && (
            <ShowInactiveButton />
          )}
          {start}
          {boardData?.session.readingsEditable && (
            <Button variant="outline" onClick={() => setActualsOpen(true)}>
              <Table />
              {OGSM_COPY.enterActuals}
            </Button>
          )}
          {structureEditable && (
            <Button
              onClick={() =>
                setDialog(
                  drilled
                    ? { mode: 'create-child', parent: drilled }
                    : { mode: 'create' }
                )
              }
            >
              <SquarePlus />
              {drilled ? OGSM_COPY.newStrategy : OGSM_COPY.newObjective}
            </Button>
          )}
        </>
      )}
      {boardData && boardData.session.editable && !drilled && !portal && (
        <SessionBar
          key={boardData.session.id}
          companyId={companyId}
          board={boardData}
        />
      )}
      {boardData && (
        <OgsmBoardView
          companyId={companyId}
          board={boardData}
          objectiveId={objectiveId}
          onDrill={(objective) =>
            onNavigate({ session: boardData.session.id, objective })
          }
          onAddObjective={
            // Drilled, the title row's New strategy adds a child; the ghost stays root-only.
            structureEditable && !drilled
              ? () => setDialog({ mode: 'create' })
              : undefined
          }
        />
      )}
      {boardData && (
        <NewObjectiveDialog
          companyId={companyId}
          session={boardData.session}
          target={dialog}
          onClose={() => setDialog(null)}
        />
      )}
      {boardData && (
        <EnterActualsDialog
          companyId={companyId}
          board={boardData}
          open={actualsOpen}
          onClose={() => setActualsOpen(false)}
        />
      )}
    </div>
  )
}
